import { useEffect, useRef, useState, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Polyline, useMap } from "react-leaflet";
import { DarkTiles } from "@/components/map/DarkTiles";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

/**
 * OrderMiniRouteMap — mini-carte intégrée pour une commande livreur.
 *
 * - Affiche la position GPS LIVE du livreur + le point de destination contextuel
 *   (pickup si status=assigned, delivery sinon).
 * - Recalcule l'itinéraire VRAI (routing OSRM) toutes les 5 secondes.
 * - Fallback ligne droite si OSRM indisponible.
 * - Nettoyage strict des intervals + abort des fetches sur unmount → zéro fuite.
 *
 * Props:
 *   - orderId        : identifiant unique (clef de cache + AbortController)
 *   - destLat/destLng : coordonnées GPS de la destination
 *   - destLabel      : "RETRAIT" | "LIVRAISON"
 *   - height         : hauteur en px (défaut 220)
 */
export default function OrderMiniRouteMap({
  orderId,
  destLat,
  destLng,
  destLabel = "DESTINATION",
  height = 220,
}) {
  const [courierPos, setCourierPos] = useState(null);
  const [routeCoords, setRouteCoords] = useState(null);
  const [routeInfo, setRouteInfo] = useState({ distanceKm: null, durationMin: null });
  const [error, setError] = useState(null);

  const abortRef = useRef(null);
  const watchIdRef = useRef(null);
  const routeIntervalRef = useRef(null);
  const lastFetchKeyRef = useRef("");

  // 1) GPS watcher : récupère et met à jour la position courrier
  useEffect(() => {
    if (!navigator.geolocation) {
      setError("Géolocalisation non supportée");
      return;
    }
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        setCourierPos({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          heading: pos.coords.heading,
        });
      },
      (err) => {
        // permission refusée ou indisponible → on garde l'écran sans position
        console.debug("[MiniMap] geolocation watch error:", err?.message);
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 }
    );
    return () => {
      if (watchIdRef.current != null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, []);

  // 2) Recalcul d'itinéraire toutes les 5s via OSRM
  useEffect(() => {
    if (!destLat || !destLng) return;

    const fetchRoute = async () => {
      const origin = courierPos;
      if (!origin) return;
      // Évite les requêtes identiques inutiles (économie d'API OSRM)
      const key = `${origin.lat.toFixed(4)},${origin.lng.toFixed(4)}|${destLat},${destLng}`;
      if (key === lastFetchKeyRef.current) return;
      lastFetchKeyRef.current = key;

      // Abort la requête précédente si toujours en cours
      if (abortRef.current) {
        try { abortRef.current.abort(); } catch (e) { /* ignore */ }
      }
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const url = `https://router.project-osrm.org/route/v1/driving/${origin.lng},${origin.lat};${destLng},${destLat}?overview=full&geometries=geojson`;
        const res = await fetch(url, { signal: controller.signal });
        if (!res.ok) throw new Error(`OSRM ${res.status}`);
        const data = await res.json();
        const route = data?.routes?.[0];
        if (!route?.geometry?.coordinates) throw new Error("Aucun itinéraire");
        // GeoJSON renvoie [lng, lat] → on flip pour Leaflet [lat, lng]
        const coords = route.geometry.coordinates.map(([lng, lat]) => [lat, lng]);
        setRouteCoords(coords);
        setRouteInfo({
          distanceKm: route.distance / 1000,
          durationMin: route.duration / 60,
        });
        setError(null);
      } catch (e) {
        if (e.name === "AbortError") return;
        // Fallback ligne droite — pas grave, l'utilisateur a quand même un aperçu
        setRouteCoords([[origin.lat, origin.lng], [destLat, destLng]]);
        setError("Itinéraire approximatif");
      }
    };

    // Premier appel immédiat, puis toutes les 5s
    fetchRoute();
    routeIntervalRef.current = setInterval(fetchRoute, 5000);
    return () => {
      if (routeIntervalRef.current) clearInterval(routeIntervalRef.current);
      if (abortRef.current) {
        try { abortRef.current.abort(); } catch (e) { /* ignore */ }
      }
    };
  }, [courierPos, destLat, destLng]);

  // Icônes mémorisées — évitent les re-créations inutiles
  const courierIcon = useMemo(
    () =>
      L.divIcon({
        className: "ak-mini-courier",
        html: `<div style="width:32px;height:32px;border-radius:50%;background:#003594;border:3px solid #D4AF37;display:flex;align-items:center;justify-content:center;box-shadow:0 0 12px rgba(212,175,55,0.6);">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M12 2L4 14h5v8h6v-8h5L12 2z"/></svg>
        </div>`,
        iconSize: [32, 32],
        iconAnchor: [16, 16],
      }),
    []
  );
  const destIcon = useMemo(
    () =>
      L.divIcon({
        className: "ak-mini-dest",
        html: `<div style="width:30px;height:30px;border-radius:50% 50% 50% 0;background:#D4AF37;border:3px solid #003594;transform:rotate(-45deg);box-shadow:0 4px 8px rgba(0,0,0,0.4);"></div>`,
        iconSize: [30, 30],
        iconAnchor: [15, 30],
      }),
    []
  );

  const center = courierPos
    ? [courierPos.lat, courierPos.lng]
    : [destLat, destLng];

  return (
    <div
      style={{ height, width: "100%", position: "relative", borderRadius: 16, overflow: "hidden" }}
      data-testid={`mini-route-map-${orderId}`}
    >
      <MapContainer
        center={center}
        zoom={13}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom={false}
        attributionControl={false}
        className="woma-dark-map"
      >
        <DarkTiles />
        <MapAutoFit
          courierPos={courierPos}
          destLat={destLat}
          destLng={destLng}
          routeCoords={routeCoords}
        />
        {courierPos && <Marker position={[courierPos.lat, courierPos.lng]} icon={courierIcon} />}
        <Marker position={[destLat, destLng]} icon={destIcon} />
        {routeCoords && routeCoords.length >= 2 && (
          <Polyline
            positions={routeCoords}
            pathOptions={{ color: "#D4AF37", weight: 5, opacity: 0.85 }}
          />
        )}
      </MapContainer>

      {/* Overlay info bottom */}
      <div
        style={{
          position: "absolute",
          bottom: 8,
          left: 8,
          right: 8,
          background: "rgba(2, 6, 23, 0.85)",
          borderRadius: 12,
          padding: "8px 12px",
          color: "#fff",
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: 1,
          textTransform: "uppercase",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pointerEvents: "none",
          zIndex: 500,
        }}
      >
        <span style={{ color: "#D4AF37" }}>→ {destLabel}</span>
        {routeInfo.distanceKm != null ? (
          <span>
            {routeInfo.distanceKm.toFixed(1)} km · ~
            {Math.max(1, Math.round(routeInfo.durationMin))} min
          </span>
        ) : (
          <span style={{ opacity: 0.7 }}>{error || "Calcul en cours…"}</span>
        )}
      </div>
    </div>
  );
}

/**
 * Sous-composant : ajuste automatiquement les bounds de la carte pour montrer
 * à la fois la position du livreur et la destination. Ré-évalué quand le tracé change.
 */
function MapAutoFit({ courierPos, destLat, destLng, routeCoords }) {
  const map = useMap();
  useEffect(() => {
    if (!map) return;
    let bounds = null;
    if (routeCoords && routeCoords.length >= 2) {
      bounds = L.latLngBounds(routeCoords);
    } else if (courierPos) {
      bounds = L.latLngBounds([
        [courierPos.lat, courierPos.lng],
        [destLat, destLng],
      ]);
    }
    if (bounds && bounds.isValid()) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16, animate: true });
    }
  }, [map, courierPos, destLat, destLng, routeCoords]);
  return null;
}
